import { useState } from 'react';
import PropTypes from 'prop-types';

import InputField from '@components/inputs/input';
import CombinedInputField from '@components/inputs/combined-input';
import Select from '@components/select';
import Link from '@components/link';
import Button from '@components/button';
import Toast from '@components/toast';
import CalculatorLayout from '../shared/calculator-layout';
import ChartTooltip from '../shared/chart-tooltip';
import CalculatorLegend from '../shared/calculator-legend';
import CalculatorLineChart from '../shared/calculator-line-chart';

import { calculateRentVsBuy, formatCurrency } from '../shared/calculator-utils';
import { RENT_VS_BUY_CONSTANTS } from '../shared/calculator-constants';
import useCalculatorInputs from '../shared/use-calculator-inputs';
import useInterestRate from '../shared/use-interest-rate';
import useLoanTerm from '../shared/use-loan-term';
import {
  LOAN_TERM_OPTIONS,
  CTA_BUTTON_CONFIG,
  DISCLAIMER_URLS
} from '../shared/calculator-ui-constants';

import styles from './styles.module.scss';
const {
  calculator_cta,
  results_header,
  savings_display,
  savings_amount,
  savings_period,
  cta_wrapper,
  tooltip_header,
  tooltip_row,
  tooltip_summary,
  select_wrapper
} = styles;

const RentVsBuyCalculator = ({
  calculatorOptions = [],
  onCalculatorChange,
  showCalculatorSelector = true,
  currentRates,
  hideVisual = false,
  condensed = false,
  title,
  description
}) => {
  const [monthlyRent, setMonthlyRent] = useState(700);
  const [yearsStaying, setYearsStaying] = useState(7);

  // Use custom hooks for calculator state management
  const sharedInputs = useCalculatorInputs(100000, 5);
  const {
    homePrice,
    downPayment,
    downPaymentPercent,
    handleHomePriceChange,
    handleDownPaymentChange: sharedHandleDownPaymentChange,
    handleDownPaymentPercentChange: sharedHandleDownPaymentPercentChange
  } = sharedInputs;

  // Allow unclamped values during typing for real-time feedback
  const handleDownPaymentChange = (value) => {
    sharedHandleDownPaymentChange(value);
  };

  const handleDownPaymentPercentChange = (value) => {
    sharedHandleDownPaymentPercentChange(value);
  };

  // Clamp to 5% minimum on blur
  const handleDownPaymentBlur = () => {
    const minDownPayment = homePrice * 0.05;
    if (downPayment < minDownPayment) {
      sharedHandleDownPaymentChange(minDownPayment);
      Toast.notify({
        message: 'Conventional loans require a minimum 5% down payment',
        type: 'info'
      });
    }
  };

  const handleDownPaymentPercentBlur = () => {
    if (downPaymentPercent < 5) {
      sharedHandleDownPaymentPercentChange(5);
      Toast.notify({
        message: 'Conventional loans require a minimum 5% down payment',
        type: 'info'
      });
    } else if (downPaymentPercent > 100) {
      sharedHandleDownPaymentPercentChange(100);
    }
  };

  const { interestRate, handleInterestRateChange, setInterestRate } = useInterestRate(
    currentRates?.[30] || 4
  );

  const { loanTerm, handleLoanTermChange } = useLoanTerm(30, currentRates, setInterestRate);

  const analysis = calculateRentVsBuy(
    homePrice,
    downPayment,
    interestRate,
    loanTerm,
    monthlyRent,
    yearsStaying
  );

  const generateChartData = () => {
    const data = [];
    const maxMonths = yearsStaying * 12;
    const loanAmount = homePrice - downPayment;
    const ltv = homePrice > 0 ? loanAmount / homePrice : 0;

    // Apply FHA upfront MIP if down payment is less than 5%
    const adjustedLoanAmount =
      ltv > 0.95 ? loanAmount * (1 + RENT_VS_BUY_CONSTANTS.UPFRONT_MIP_RATE) : loanAmount;

    // Key assumptions
    const homeAppreciationRate = 0.03;
    const rentAppreciationRate = 0.035;
    const investmentReturnRate = 0.035;

    // Calculate mortgage payment components
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    const monthlyPI =
      (adjustedLoanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    const monthlyTaxes = (homePrice * 0.02) / 12;
    const monthlyInsurance = (homePrice * 0.006) / 12;

    // Calculate PMI
    let monthlyPMI = 0;
    if (ltv > 0.9) {
      monthlyPMI = (adjustedLoanAmount * 0.0089) / 12;
    } else if (ltv > 0.85) {
      monthlyPMI = (adjustedLoanAmount * 0.0057) / 12;
    } else if (ltv > 0.8) {
      monthlyPMI = (adjustedLoanAmount * 0.0033) / 12;
    }

    const totalMonthlyPayment = monthlyPI + monthlyTaxes + monthlyInsurance + monthlyPMI;

    // Calculate net position at each month
    for (let month = 0; month <= maxMonths; month += 1) {
      const yearsElapsed = month / 12;

      // Ownership cost if you sell at this month
      // Month 0 = closing day (you pay down payment and can immediately sell if needed)
      let totalPayments = 0;
      let maintenanceToDate = 0;
      let balanceAtMonth = adjustedLoanAmount;

      // Accumulate payments from month 1 onwards (first payment is due after first month)
      for (let m = 1; m <= month; m++) {
        const yearsElapsedAtMonth = m / 12;
        const appreciationFactor = Math.pow(1 + homeAppreciationRate, yearsElapsedAtMonth);

        totalPayments += totalMonthlyPayment;
        maintenanceToDate += (homePrice * 0.01 * appreciationFactor) / 12;

        const monthlyInterest = balanceAtMonth * monthlyRate;
        const principalPayment = monthlyPI - monthlyInterest;
        balanceAtMonth = Math.max(0, balanceAtMonth - principalPayment);
      }

      // Calculate home value and sale proceeds at this month
      const homeValueNow = homePrice * Math.pow(1 + homeAppreciationRate, yearsElapsed);
      const sellingCostsNow = homeValueNow * 0.06;
      const proceedsFromSale = homeValueNow - balanceAtMonth - sellingCostsNow;

      // Calculate opportunity cost of down payment
      const downPaymentGrowth = downPayment * Math.pow(1 + investmentReturnRate, yearsElapsed);
      const opportunityCost = downPaymentGrowth - downPayment;

      // Net ownership cost = what you paid + opportunity cost - what you get back
      const netOwnershipCost =
        downPayment + totalPayments + maintenanceToDate + opportunityCost - proceedsFromSale;

      // Rental cost up to this month
      // Month 0 = decision day (haven't paid any rent yet)
      let cumulativeRentalCost = 0;
      for (let m = 1; m <= month; m++) {
        const yearsElapsedAtMonth = m / 12;
        const rentFactor = Math.pow(1 + rentAppreciationRate, yearsElapsedAtMonth);
        const adjustedRent = monthlyRent * rentFactor;
        cumulativeRentalCost += adjustedRent + adjustedRent * 0.0132;
      }

      // Net rental cost = rental payments - investment gains (renter keeps down payment invested)
      const netRentalCost = cumulativeRentalCost - opportunityCost;

      data.push({
        month: month,
        year: (month / 12).toFixed(1),
        ownership: netOwnershipCost,
        rental: netRentalCost
      });
    }

    return data;
  };

  const chartData = generateChartData();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const ownershipCost = payload.find((p) => p.dataKey === 'ownership')?.value || 0;
      const rentalCost = payload.find((p) => p.dataKey === 'rental')?.value || 0;
      const isBreakEven = Math.abs(label - analysis.breakEvenMonths) < 1;

      return (
        <ChartTooltip>
          <p className={tooltip_header}>
            Month {label}
            {isBreakEven && ' (Break-Even)'}
          </p>
          <p className={`${tooltip_row} ownership`}>Owning: {formatCurrency(ownershipCost)}</p>
          <p className={`${tooltip_row} rental`}>Renting: {formatCurrency(rentalCost)}</p>
          <p className={tooltip_summary}>
            {rentalCost > ownershipCost
              ? `Owning saves: ${formatCurrency(rentalCost - ownershipCost)}`
              : `Renting saves: ${formatCurrency(ownershipCost - rentalCost)}`}
          </p>
        </ChartTooltip>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.object),
    label: PropTypes.number
  };

  const disclaimerContent = (
    <>
      *Rent vs. Buy analysis is based on estimated values and assumptions.
      <br />
      Results show average monthly cost over the selected time period.{' '}
      <Link url={DISCLAIMER_URLS.findLoanOfficer}>Contact a loan officer</Link> for a personalized
      home buying analysis.
    </>
  );

  const resultsContent = (
    <>
      <h3 className={results_header}>
        {analysis.recommendation === 'Buy' ? 'Owning' : 'Renting'} could save you
      </h3>

      <div className={savings_display}>
        <div className={savings_amount}>{formatCurrency(analysis.monthlySavings)}</div>
        <div className={savings_period}>/month*</div>
      </div>

      <CalculatorLegend
        items={[
          { label: 'Owning', value: analysis.ownershipMonthlyCost, suffix: '/month*' },
          { label: 'Renting', value: analysis.rentalMonthlyCost, suffix: '/month*' }
        ]}
        condensed={condensed}
        hideVisual={hideVisual}
      />

      <CalculatorLineChart
        data={chartData}
        lines={[
          { dataKey: 'rental', stroke: '#8B0000', name: 'Renting' },
          { dataKey: 'ownership', stroke: '#666', name: 'Owning' }
        ]}
        yAxisFormatter={(value) => `$${Math.round(value / 1000)}k`}
        tooltip={<CustomTooltip />}
        condensed={condensed}
        hideVisual={hideVisual}
      />

      <div className={cta_wrapper}>
        <Button
          type="button"
          label={CTA_BUTTON_CONFIG.label}
          url={CTA_BUTTON_CONFIG.url}
          theme={CTA_BUTTON_CONFIG.theme}
          className={calculator_cta}
        />
      </div>
    </>
  );

  return (
    <CalculatorLayout
      title={title || 'Rent vs. Buy Calculator'}
      description={description || 'Compare the long-term costs of renting versus buying a home'}
      calculatorType="rent-vs-buy"
      calculatorOptions={calculatorOptions}
      onCalculatorChange={onCalculatorChange}
      showCalculatorSelector={showCalculatorSelector}
      hideVisual={hideVisual}
      condensed={condensed}
      resultsContent={resultsContent}
      disclaimerContent={disclaimerContent}
    >
      <InputField
        label="Home Purchase Price"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={homePrice.toString()}
        defaultOnChange={handleHomePriceChange}
        inputName="homePrice"
      />

      <CombinedInputField
        label="Down Payment"
        leftInputName="downPayment"
        rightInputName="downPaymentPercent"
        defaultLeftValue={downPayment}
        defaultRightValue={downPaymentPercent}
        onLeftChange={handleDownPaymentChange}
        onRightChange={handleDownPaymentPercentChange}
        onLeftBlur={handleDownPaymentBlur}
        onRightBlur={handleDownPaymentPercentBlur}
      />

      <InputField
        label="Interest Rate"
        inputType="text"
        inputFormat="percentage"
        defaultInputValue={interestRate.toString()}
        defaultOnChange={handleInterestRateChange}
        inputName="interestRate"
      />

      <div className={select_wrapper}>
        <Select
          id="loanTerm"
          label="Loan Term"
          options={LOAN_TERM_OPTIONS}
          selected={loanTerm}
          onChange={handleLoanTermChange}
        />
      </div>

      <InputField
        label="Monthly Rent"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={monthlyRent.toString()}
        defaultOnChange={(value) => setMonthlyRent(parseFloat(value) || 0)}
        inputName="monthlyRent"
      />

      <InputField
        label="Years Staying"
        inputType="number"
        defaultInputValue={yearsStaying.toString()}
        defaultOnChange={(value) => {
          const parsed = parseInt(value);
          if (!isNaN(parsed) && value !== '') {
            setYearsStaying(Math.max(1, Math.min(30, parsed)));
          }
        }}
        inputName="yearsStaying"
      />
    </CalculatorLayout>
  );
};

RentVsBuyCalculator.propTypes = {
  calculatorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onCalculatorChange: PropTypes.func,
  showCalculatorSelector: PropTypes.bool,
  currentRates: PropTypes.shape({
    15: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    30: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  hideVisual: PropTypes.bool,
  condensed: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string
};

export default RentVsBuyCalculator;
